import Big from 'big.js'

import { AppliedCreditNote, ServerLineItem } from 'Invoices/types'

import { CreditNoteLineItemRow } from './CreditNoteLineItemTable'

const hasFullApplicationAmount = (creditNote: AppliedCreditNote): boolean =>
  new Big(creditNote.appliedAmount).eq(creditNote.grossTotal)

/** Original line totals cannot identify which lines remain credited after a partial application. */
export const hasPartiallyAppliedLinkedCreditNote = (
  creditNotes: AppliedCreditNote[]
): boolean =>
  creditNotes.some(creditNote => {
    const isPartiallyApplied = !hasFullApplicationAmount(creditNote)
    const hasLinkedLine = creditNote.lineItems.some(
      lineItem => lineItem.invoiceLineItemIds?.length === 1
    )
    return isPartiallyApplied && hasLinkedLine
  })

/**
 * Net amount still creditable per invoice line item: the line item's net total
 * minus fully applied credit notes attributed to it. Partial applications have
 * no reliable per-line amount, so the invoice balance caps those credits. This
 * calculation seeds drawer rows and validates that a row does not exceed its
 * line item. The result can be negative when previous credits exceed the line
 * item; callers decide how to floor it.
 */
export const remainingNetByLineItem = (
  lineItems: ServerLineItem[],
  appliedCreditNotes: AppliedCreditNote[]
): Map<string, Big> => {
  const credited = creditedNetByLineItem(appliedCreditNotes)

  return lineItems.reduce(
    (remaining, lineItem) =>
      remaining.set(
        lineItem.id,
        new Big(lineItem.netTotal).minus(credited.get(lineItem.id) ?? 0)
      ),
    new Map<string, Big>()
  )
}

/**
 * Rows whose net amount exceeds the remaining creditable net of their invoice
 * line item, keyed by rowId with the remaining amount (floored at zero, money
 * precision) for display in the inline validation hint. Manually added rows
 * (no source line item) have no per-line cap; the aggregate balance check in
 * getCreditNoteTotals still applies to them. Compared in whole cents to avoid
 * float noise, mirroring getCreditNoteTotals.
 */
export const overCreditedRows = (
  rows: CreditNoteLineItemRow[],
  remainingNet: Map<string, Big>
): Map<string, string> =>
  rows.reduce((overCredited, row) => {
    const remaining = row.lineItemId
      ? remainingNet.get(row.lineItemId)
      : undefined

    if (!row.checked || remaining === undefined) {
      return overCredited
    }

    const priceCents = Math.round(Number(row.price || 0) * 100)
    const remainingCents = Math.round(remaining.toNumber() * 100)

    if (priceCents <= remainingCents) {
      return overCredited
    }

    const displayRemaining = remaining.lt(0) ? new Big(0) : remaining

    return overCredited.set(row.rowId, displayRemaining.toFixed(2))
  }, new Map<string, string>())

/**
 * Net amount previously credited per invoice line item, summed across all
 * fully applied credit notes. Only credit note line items linked to exactly one
 * invoice line item are attributable; legacy items (empty or absent
 * invoiceLineItemIds) and multi-line items are excluded here, though they
 * still count toward the aggregate balance cap in getCreditNoteTotals.
 */
const creditedNetByLineItem = (
  appliedCreditNotes: AppliedCreditNote[]
): Map<string, Big> =>
  appliedCreditNotes
    .filter(hasFullApplicationAmount)
    .flatMap(creditNote => creditNote.lineItems)
    .reduce((credited, creditNoteLineItem) => {
      const invoiceLineItemIds = creditNoteLineItem.invoiceLineItemIds ?? []
      const invoiceLineItemId =
        invoiceLineItemIds.length === 1 ? invoiceLineItemIds.at(0) : undefined

      if (!invoiceLineItemId) {
        return credited
      }

      const previous = credited.get(invoiceLineItemId) ?? new Big(0)

      return credited.set(
        invoiceLineItemId,
        previous.plus(creditNoteLineItem.netTotal)
      )
    }, new Map<string, Big>())
